import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Layers, Loader2, RefreshCw, Play, Trash2, CheckCircle2, XCircle, Clock, AlertTriangle, Building2,
} from 'lucide-react';

const FACTURA_TIPO_OPTS = [
  { v: '1', l: '01 - Factura A', cond: 'ri' },
  { v: '6', l: '06 - Factura B', cond: 'ri' },
  { v: '11', l: '11 - Factura C', cond: 'monotributo' },
];
const DOC_TIPOS = [{ v: '80', l: '80 - CUIT' }, { v: '86', l: '86 - CUIL' }, { v: '96', l: '96 - DNI' }, { v: '99', l: '99 - Consumidor Final' }];
const COND_IVA_RECEPTOR = [
  { v: '1', l: 'IVA Responsable Inscripto' }, { v: '6', l: 'Responsable Monotributo' }, { v: '4', l: 'IVA Sujeto Exento' },
  { v: '5', l: 'Consumidor Final' }, { v: '13', l: 'Monotributista Social' }, { v: '7', l: 'Sujeto No Categorizado' },
];
const ALICUOTAS = [{ v: '21', l: '21%' }, { v: '10.5', l: '10,5%' }, { v: '27', l: '27%' }, { v: '0', l: '0%' }];

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
};
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; } };

const ESTADO_LOTE = {
  pendiente: { l: 'Pendiente de facturar', cls: 'bg-slate-500' },
  ejecutando: { l: 'Ejecutando…', cls: 'bg-amber-500' },
  completado: { l: 'Completado', cls: 'bg-emerald-600' },
  completado_con_errores: { l: 'Completado con errores', cls: 'bg-orange-600' },
};

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const LotesTab = ({ emisores }) => {
  const listos = emisores.filter((e) => e.has_cert);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ emisor_id: '', pto_vta: '', cbte_tipo: '6', doc_tipo: '80', condicion_iva_receptor_id: '5', iva_alicuota: '21', iva_incluido: true, cbte_fch: hoyISO() });
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/arca/facturacion/lotes');
      setLotes(res.data || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || 'Error cargando lotes');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const loadLote = useCallback(async (id) => {
    try {
      const res = await api.get(`/arca/facturacion/lotes/${id}`);
      setSel(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const abrir = async (l) => {
    const full = await loadLote(l.id);
    if (!full) { toast.error('No se pudo abrir el lote'); return; }
    const emDefault = listos[0];
    setForm({
      emisor_id: full.emisor_id || '', pto_vta: full.pto_vta != null ? String(full.pto_vta) : (emDefault?.default_config?.pto_vta != null ? String(emDefault.default_config.pto_vta) : ''),
      cbte_tipo: full.cbte_tipo != null ? String(full.cbte_tipo) : '6',
      doc_tipo: full.doc_tipo != null ? String(full.doc_tipo) : '80',
      condicion_iva_receptor_id: full.condicion_iva_receptor_id != null ? String(full.condicion_iva_receptor_id) : '5',
      iva_alicuota: full.iva_alicuota || '21', iva_incluido: full.iva_incluido !== undefined ? !!full.iva_incluido : true,
      cbte_fch: full.cbte_fch && String(full.cbte_fch).length === 8
        ? `${String(full.cbte_fch).slice(0, 4)}-${String(full.cbte_fch).slice(4, 6)}-${String(full.cbte_fch).slice(6, 8)}`
        : hoyISO(),
    });
    setOpen(true);
  };

  // Polling mientras un lote está ejecutando
  useEffect(() => {
    if (open && sel && sel.estado === 'ejecutando') {
      pollRef.current = setInterval(async () => {
        const full = await loadLote(sel.id);
        if (full && full.estado !== 'ejecutando') { clearInterval(pollRef.current); loadList(); }
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [open, sel?.id, sel?.estado, loadLote, loadList]);

  const pickEmpresa = (id) => {
    const em = listos.find((e) => e.id === id);
    setForm((p) => ({ ...p, emisor_id: id, pto_vta: p.pto_vta || (em?.default_config?.pto_vta != null ? String(em.default_config.pto_vta) : '') }));
  };

  const ejecutar = async () => {
    if (!form.emisor_id) { toast.error('Elegí la empresa que va a facturar'); return; }
    if (!form.pto_vta) { toast.error('Ingresá el punto de venta'); return; }
    if (!form.cbte_fch) { toast.error('Elegí la fecha de emisión'); return; }
    setStarting(true);
    try {
      await api.post(`/arca/facturacion/lotes/${sel.id}/ejecutar`, {
        emisor_id: form.emisor_id, pto_vta: Number(form.pto_vta), cbte_tipo: Number(form.cbte_tipo),
        doc_tipo: Number(form.doc_tipo), condicion_iva_receptor_id: Number(form.condicion_iva_receptor_id),
        iva_alicuota: form.iva_alicuota, iva_incluido: !!form.iva_incluido,
        cbte_fch: (form.cbte_fch || '').replace(/-/g, ''),
      });
      toast.success('Facturación iniciada');
      await new Promise((r) => setTimeout(r, 800));
      await loadLote(sel.id);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar'); } finally { setStarting(false); }
  };

  const borrar = async (l, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el lote de ${l.total} comprobante(s)?`)) return;
    try { await api.delete(`/arca/facturacion/lotes/${l.id}`); toast.success('Lote eliminado'); loadList(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const empresaDelLote = sel?.emisor_id ? emisores.find((e) => e.id === sel.emisor_id) : null;

  return (
    <div className="space-y-4" data-testid="fact-lotes-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Lotes de facturación enviados desde Cobranzas de Préstamos.</p>
        <Button variant="outline" size="sm" onClick={loadList} disabled={loading}><RefreshCw size={15} className="mr-1" /> Actualizar</Button>
      </div>

      {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        : lotes.length === 0 ? <Card className="p-8 text-center text-slate-400 text-sm">No hay lotes todavía. Generá uno desde Préstamos → Cobranzas seleccionando clientes y tocando "Facturar".</Card>
        : (
          <div className="space-y-2">
            {lotes.map((l) => {
              const est = ESTADO_LOTE[l.estado] || ESTADO_LOTE.pendiente;
              const pct = l.total ? Math.round((l.procesados / l.total) * 100) : 0;
              return (
                <Card key={l.id} className="p-4 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => abrir(l)} data-testid={`fact-lote-${l.id}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Layers size={18} className="text-slate-400" />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">Lote {l.mes_label || l.periodo}</span>
                          <Badge className={est.cls} data-testid={`fact-lote-estado-${l.id}`}>{est.l}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{l.total} comprobante(s) · creado {fmt(l.created_at)}{l.creado_por ? ` · ${l.creado_por}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {l.estado !== 'pendiente' && (
                        <div className="text-right text-xs">
                          <span className="text-emerald-600 font-medium">{l.ok_count} ok</span>
                          {l.error_count > 0 && <span className="text-rose-600 font-medium ml-2">{l.error_count} error</span>}
                          <span className="text-slate-400 ml-2">{l.procesados}/{l.total}</span>
                        </div>
                      )}
                      {l.estado === 'pendiente' ? (
                        <>
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); abrir(l); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`fact-lote-ejecutar-${l.id}`}><Play size={14} className="mr-1" /> Ejecutar</Button>
                          <Button variant="ghost" size="icon" onClick={(e) => borrar(l, e)} data-testid={`fact-lote-del-${l.id}`}><Trash2 size={15} className="text-rose-500" /></Button>
                        </>
                      ) : l.estado === 'ejecutando' ? (
                        <div className="w-40"><Progress value={pct} className="h-2" /></div>
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

      {/* Detalle / ejecución del lote */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); clearInterval(pollRef.current); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fact-lote-dialog">
          <DialogHeader>
            <DialogTitle>Lote {sel?.mes_label || sel?.periodo}</DialogTitle>
            <DialogDescription>{sel?.total} comprobante(s) · {(ESTADO_LOTE[sel?.estado] || {}).l}</DialogDescription>
          </DialogHeader>

          {sel && sel.estado === 'pendiente' && (
            <div className="space-y-4">
              <Card className="p-4 bg-slate-50 border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-3">Datos de emisión (podés cambiarlos)</p>
                {listos.length === 0 ? (
                  <p className="text-sm text-amber-700 flex items-center gap-2"><AlertTriangle size={16} /> No hay ninguna empresa con certificado cargado. Cargá el certificado en la pestaña Empresas.</p>
                ) : (
                  <div className="grid md:grid-cols-3 gap-3">
                    <div><Label>Empresa que factura</Label>
                      <Select value={form.emisor_id} onValueChange={pickEmpresa}>
                        <SelectTrigger className="mt-1" data-testid="fact-lote-empresa"><SelectValue placeholder="Elegí…" /></SelectTrigger>
                        <SelectContent>{listos.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social} · {e.environment === 'produccion' ? 'PROD' : 'HOMO'}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Punto de venta</Label><Input value={form.pto_vta} onChange={(e) => setForm({ ...form, pto_vta: e.target.value.replace(/\D/g, '') })} className="font-mono mt-1" placeholder="1" data-testid="fact-lote-ptovta" /></div>
                    <div><Label>Tipo de comprobante</Label>
                      <Select value={form.cbte_tipo} onValueChange={(v) => setForm({ ...form, cbte_tipo: v })}>
                        <SelectTrigger className="mt-1" data-testid="fact-lote-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>{FACTURA_TIPO_OPTS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Tipo doc receptor</Label>
                      <Select value={form.doc_tipo} onValueChange={(v) => setForm({ ...form, doc_tipo: v })}>
                        <SelectTrigger className="mt-1" data-testid="fact-lote-doctipo"><SelectValue /></SelectTrigger>
                        <SelectContent>{DOC_TIPOS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Condición IVA receptor</Label>
                      <Select value={form.condicion_iva_receptor_id} onValueChange={(v) => setForm({ ...form, condicion_iva_receptor_id: v })}>
                        <SelectTrigger className="mt-1" data-testid="fact-lote-cond"><SelectValue /></SelectTrigger>
                        <SelectContent>{COND_IVA_RECEPTOR.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Alícuota IVA</Label>
                      <Select value={form.iva_alicuota} onValueChange={(v) => setForm({ ...form, iva_alicuota: v })}>
                        <SelectTrigger className="mt-1" data-testid="fact-lote-alic"><SelectValue /></SelectTrigger>
                        <SelectContent>{ALICUOTAS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Fecha de emisión</Label>
                      <Input type="date" value={form.cbte_fch} onChange={(e) => setForm({ ...form, cbte_fch: e.target.value })} className="mt-1" data-testid="fact-lote-fecha" />
                      <p className="text-[11px] text-slate-400 mt-1">Fecha del comprobante (ARCA acepta ±5 días para productos).</p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-3">El importe de cada comprobante = la <b>Ganancia</b> del cliente (IVA incluido). El Nº de documento receptor = el CUIT que vino de Préstamos. Descripción: <b>{sel.descripcion_base} - {sel.mes_label}</b>.</p>
              </Card>

              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-60 overflow-y-auto divide-y">
                  {(sel.items || []).map((it) => (
                    <div key={it.item_id} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div><span className="font-medium text-slate-700">{it.cliente || '—'}</span><span className="font-mono text-xs text-slate-400 ml-2">{it.cuit}</span></div>
                      <span className="font-mono text-slate-600">{money(it.monto)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
                <Button onClick={ejecutar} disabled={starting || listos.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fact-lote-ejecutar-btn">
                  {starting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />} Ejecutar facturación
                </Button>
              </DialogFooter>
            </div>
          )}

          {sel && sel.estado !== 'pendiente' && (
            <div className="space-y-4">
              <Card className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    {empresaDelLote && <><Building2 size={15} /> {empresaDelLote.razon_social}</>}
                  </div>
                  <div className="text-sm">
                    <span className="text-emerald-600 font-semibold">{sel.ok_count} ok</span>
                    {sel.error_count > 0 && <span className="text-rose-600 font-semibold ml-3">{sel.error_count} con error</span>}
                    <span className="text-slate-400 ml-3">{sel.procesados}/{sel.total}</span>
                  </div>
                </div>
                <Progress value={sel.total ? Math.round((sel.procesados / sel.total) * 100) : 0} className="h-2.5" data-testid="fact-lote-progress" />
                {sel.estado === 'ejecutando' && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Emitiendo… podés cerrar esta ventana, sigue en segundo plano.</p>}
              </Card>

              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-72 overflow-y-auto divide-y">
                  {(sel.items || []).map((it) => (
                    <div key={it.item_id} className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`fact-lote-item-${it.item_id}`}>
                      <div className="min-w-0">
                        <span className="font-medium text-slate-700">{it.cliente || '—'}</span>
                        <span className="font-mono text-xs text-slate-400 ml-2">{it.cuit}</span>
                        {it.estado === 'error' && it.error && <p className="text-xs text-rose-600 mt-0.5 truncate max-w-md">{it.error}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-slate-600">{money(it.monto)}</span>
                        {it.estado === 'ok' ? <Badge className="bg-emerald-600"><CheckCircle2 size={12} className="mr-1" /> CAE {it.cae}</Badge>
                          : it.estado === 'error' ? <Badge className="bg-rose-600"><XCircle size={12} className="mr-1" /> Error</Badge>
                          : <Badge variant="outline" className="text-slate-500"><Clock size={12} className="mr-1" /> Pendiente</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setOpen(false); clearInterval(pollRef.current); }}>Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LotesTab;
